/**
 * ProductDetailPage Component - Story 7.3: Create Product Detail Page
 * Main page component for displaying detailed product information
 */

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, RefreshCw, Home, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchPublicProductById } from '../services/marketplacePublicService';
import { ProductImageGallery } from './ProductImageGallery';
import { ProductInfo } from './ProductInfo';
import { SellerInfo } from './SellerInfo';
import { InquiryForm } from './InquiryForm';
import { ErrorMessage } from './ErrorMessage';
import type { ProductDetailPageProps, ProductDetailData } from '../types/publicMarketplace.types';

/**
 * ProductDetailPage Component
 * Displays comprehensive product details with inquiry form
 *
 * Features:
 * - Breadcrumb navigation
 * - Product image gallery
 * - Comprehensive product information
 * - Seller information (without contact details)
 * - Inquiry submission form
 * - Loading skeleton
 * - Error handling with retry
 * - 404 product not found state
 * - Responsive layout (mobile + desktop)
 * - Back navigation
 *
 * Layout:
 * - Desktop: Two columns (60/40 split) - Images + Product Info | Seller + Inquiry Form
 * - Mobile: Stacked layout
 *
 * @component
 * @example
 * <Route path="/marketplace/products/:externalId" element={<ProductDetailPage />} />
 */
export const ProductDetailPage: React.FC<ProductDetailPageProps> = ({ className }) => {
  const { externalId } = useParams<{ externalId: string }>();
  const navigate = useNavigate();

  /**
   * Fetch product details using React Query
   */
  const {
    data: product,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<ProductDetailData>({
    queryKey: ['product-detail', externalId],
    queryFn: async () => {
      if (!externalId) {
        throw new Error('Product ID is required');
      }
      const result = await fetchPublicProductById(externalId);

      // Transform to ProductDetailData format
      // Assuming the API returns seller info in the location or a separate field
      // This is a placeholder - adjust based on actual API response
      const detailData: ProductDetailData = {
        ...result,
        seller: {
          id: 'seller-id', // TODO: Get from API response
          name: 'Seller Name', // TODO: Get from API response
          district: result.location.district,
          state: result.location.state,
        },
        inquiryCount: 0, // TODO: Get from API response if available
      };

      return detailData;
    },
    enabled: !!externalId,
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  /**
   * Navigate back to products listing
   */
  const handleBack = () => {
    navigate('/marketplace/products');
  };

  /**
   * Navigate to marketplace home
   */
  const handleGoHome = () => {
    navigate('/marketplace/home');
  };

  /**
   * Handle inquiry submission success
   */
  const handleInquirySuccess = () => {
    // Scroll to top to show success message
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={cn('min-h-screen bg-gray-50', className)}>
      {/* Header with Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 max-w-7xl">
          {/* Back Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="mb-3 -ml-2"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Products
          </Button>

          {/* Breadcrumb - Custom Implementation */}
          <nav className="flex items-center gap-2 text-sm text-gray-600">
            <button
              onClick={handleGoHome}
              className="flex items-center gap-1 hover:text-gray-900 cursor-pointer"
              type="button"
            >
              <Home className="h-4 w-4" />
              Marketplace
            </button>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <button
              onClick={handleBack}
              className="hover:text-gray-900 cursor-pointer"
              type="button"
            >
              Products
            </button>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <span className="text-gray-900 font-medium">
              {isLoading ? 'Loading...' : product?.name || 'Product Details'}
            </span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Loading State */}
        {isLoading && <ProductDetailSkeleton />}

        {/* Error State */}
        {isError && (
          <div className="max-w-2xl mx-auto">
            <div className="space-y-4">
              <ErrorMessage
                statusCode={error && 'statusCode' in error ? (error as any).statusCode : undefined}
                message={error instanceof Error ? error.message : undefined}
                onRetry={() => refetch()}
                variant="default"
              />
              <div className="flex gap-3">
                <Button variant="outline" size="sm" onClick={handleBack}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Products
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Product Content */}
        {product && !isLoading && !isError && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Images and Product Info (2/3 width on desktop) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Product Image Gallery */}
              <ProductImageGallery images={product.images} productName={product.name} />

              {/* Product Information */}
              <ProductInfo product={product} />
            </div>

            {/* Right Column: Seller Info and Inquiry Form (1/3 width on desktop) */}
            <div className="space-y-6">
              {/* Seller Information */}
              <SellerInfo seller={product.seller} />

              {/* Inquiry Form */}
              <InquiryForm
                productId={product.id}
                productName={product.name}
                minimumOrderQuantity={product.minimumOrderQuantity}
                unit={product.unit}
                onSuccess={handleInquirySuccess}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ProductDetailSkeleton Component
 * Loading skeleton for product detail page
 */
const ProductDetailSkeleton: React.FC = () => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Left Column Skeleton */}
      <div className="lg:col-span-2 space-y-6">
        {/* Image Gallery Skeleton */}
        <div className="space-y-4">
          <Skeleton className="aspect-square w-full rounded-lg" />
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="aspect-square w-20 rounded-md" />
            ))}
          </div>
        </div>

        {/* Product Info Skeleton */}
        <div className="bg-white rounded-lg border p-6 space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-6 w-1/4" />
          <Skeleton className="h-12 w-1/3" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      </div>

      {/* Right Column Skeleton */}
      <div className="space-y-6">
        {/* Seller Info Skeleton */}
        <div className="bg-white rounded-lg border p-6 space-y-4">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
        </div>

        {/* Inquiry Form Skeleton */}
        <div className="bg-white rounded-lg border p-6 space-y-4">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
